import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { type ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';

interface CandidateAsyncProps {
  children: ReactNode;
  errorDescription: string;
  errorTitle: string;
  loadingDescription: string;
  resetKeys: string[];
}

export function CandidateAsync({
  children,
  errorDescription,
  errorTitle,
  loadingDescription,
  resetKeys,
}: CandidateAsyncProps) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          fallbackRender={({ resetErrorBoundary }) => (
            <HMLoadError
              description={errorDescription}
              onRetry={resetErrorBoundary}
              title={errorTitle}
            />
          )}
          onReset={reset}
          resetKeys={resetKeys}
        >
          <Suspense
            fallback={<CandidateLoading description={loadingDescription} />}
          >
            {children}
          </Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

function CandidateLoading({ description }: { description: string }) {
  return (
    <div aria-busy="true" className="flex flex-col gap-6" role="status">
      <Skeleton className="h-4 w-40" />
      <div className="flex flex-col gap-6">
        {Array.from({ length: 3 }, (_, index) => (
          <div
            className="grid grid-cols-[16px_minmax(0,1fr)] gap-x-5"
            key={index}
          >
            <div className="flex flex-col items-center">
              <Skeleton className="size-2.5 rounded-full" />
              {index < 2 && (
                <Skeleton className="mt-2 min-h-10 w-0.5 flex-1 rounded-none" />
              )}
            </div>
            <div className="flex flex-col gap-2 pb-2">
              <Skeleton className="h-4 w-2/5 min-w-32" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          </div>
        ))}
      </div>
      <span className="sr-only">{description}</span>
    </div>
  );
}
